import { AiResponse, type AiResponseList } from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { OpenAIClientService } from '../../tools/helpers';
import { queryKeys } from './constants';
import { type EntityFilter } from './utils';

export type AiResponseFilter = EntityFilter<AiResponse>;

export const useAiResponseQuery = ({
  enabled = true,
  filters,
}: {
  enabled?: boolean;
  filters: AiResponseFilter;
}) => {
  return useQuery({
    queryKey: [queryKeys.aiResponse.root, queryKeys.aiResponse.getHash(filters)],
    queryFn: async () => {
      const response = await OpenAIClientService.BatchGetAiResponse(AiResponse.create(filters));
      return response || ({ results: [], totalCount: 0 } as AiResponseList);
    },
    enabled,
  });
};

export const useGetAiResponseQuery = ({
  enabled = true,
  filters,
}: {
  enabled?: boolean;
  filters: AiResponseFilter;
}) => {
  return useQuery({
    queryKey: [queryKeys.aiResponse.root, 'get', queryKeys.aiResponse.getHash(filters)],
    queryFn: async () => {
      const response = await OpenAIClientService.GetAiResponse(AiResponse.create(filters));
      return response;
    },
    enabled,
  });
};

export const useCreateAiResponseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (req: AiResponse) => {
      const response = await OpenAIClientService.CreateAiResponse(req);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.aiResponse.root] });
    },
  });
};

export const useUpdateAiResponseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (req: AiResponse) => {
      const response = await OpenAIClientService.UpdateAiResponse(req);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.aiResponse.root] });
    },
  });
};

export const useDeleteAiResponseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (req: AiResponse) => {
      const response = await OpenAIClientService.DeleteAiResponse(req);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.aiResponse.root] });
    },
  });
};
